import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Avatar visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Avatar" component set (node `2859:87412`).
 *
 * Spec provenance: the Dev Mode MCP server was unavailable when this was
 * built, so the geometry was measured from the published sticker sheet at
 * 400% zoom (1 design px = 8 screen px — ±0.5px accuracy, snapped to the
 * design-system scale) and the colours verified by side-by-side swatch
 * comparison against `brand-colors.json`. Re-verify via
 * `mcp__Figma__get_design_context` / `get_variable_defs` on node `2859:87412`
 * if anything ever looks off.
 *
 * Axes:
 *   size:    2xs (20px) · xs (28) · s (32) · m (36) · l (40) · xl (44) ·
 *            2xl (48) · 3xl (52) — the eight columns of the sticker sheet.
 *   variant: image | initials — INTERNAL, computed from `src` + load state
 *            (User 1–4 rows vs the "Initials" fallback row in Figma), never a
 *            public prop.
 *
 * Colours (mode-invariant, like Button's colored chips): the initials chip's
 * background + text are applied dynamically per-name from the deck palette —
 * see {@link file://./avatarColors.ts}. The `variant` axis here only toggles
 * image vs initials structure; it no longer carries the colour.
 */
export const avatarVariants = cva(
  // `inline-flex flex-row` + centring overrides the View primitive's reset
  // (`flex flex-col items-stretch`); `overflow-hidden` clips the photo to the
  // circle; `shrink-0` keeps the circle round inside flex rows.
  // Deliberately NOT `relative`: on web, a positioned avatar paints above
  // static siblings, which broke facepiles (deck-coloured border rings
  // vanished at the overlaps and the "+N" overflow chip fell behind the
  // photos). RN doesn't need the class either — views default to relative,
  // so AvatarImage.native's absoluteFill still anchors correctly.
  'inline-flex shrink-0 flex-row items-center justify-center overflow-hidden rounded-full',
  {
    variants: {
      size: {
        '2xs': 'h-5 w-5',
        xs: 'h-7 w-7',
        s: 'h-8 w-8',
        m: 'h-9 w-9',
        l: 'h-10 w-10',
        xl: 'h-11 w-11',
        '2xl': 'h-12 w-12',
        // 52px has no standard Tailwind step (h-13 is v4-only).
        '3xl': 'h-[52px] w-[52px]',
        // 125px hero — the account/profile screen's identity block, measured
        // from the design (file `v4J1tu2TRhJNVVPRc5EI72`, node `114:46903`).
        // A real size because the screen was already rendering a hero, faking
        // it by overriding h-/w- onto a 3xl and setting the initials scale at
        // the call site.
        '4xl': 'h-[125px] w-[125px]',
      },
      variant: {
        image: '',
        // Background colour is applied per-name in Avatar.tsx (avatarColors).
        initials: '',
      },
    },
    defaultVariants: { size: 'm', variant: 'initials' },
  },
);

/**
 * Initials typography per avatar size. Mona Sans SemiBold (stem-width
 * measured ≈0.105em on the sheet — SemiBold, not Bold), `#f5f3ff` like
 * Button's on-primary label. XL and 2XL share 18px — adjacent sizes sharing a
 * font step has precedent in the DS (Badge S and M both use 12px).
 * `!leading-[normal]` avoids RN ascender clipping (same as Badge).
 */
export const avatarInitialsVariants = cva(
  'whitespace-nowrap font-semibold !leading-[normal] text-stable-primary-50 native:font-mona-semibold',
  {
    variants: {
      size: {
        '2xs': 'text-[8px]',
        xs: 'text-[10px]',
        s: 'text-xs',
        m: 'text-sm',
        l: 'text-base',
        xl: 'text-lg',
        '2xl': 'text-lg',
        '3xl': 'text-xl',
        // iOS renders this oversized single line of caps a few points high in
        // the circle (empty descender space below), so the hero nudges down to
        // optically centre. iOS-scoped so it doesn't fight the Android
        // font-padding fix in Avatar.tsx; a no-op on web.
        // 48px on a 125px circle — the same ~0.38 glyph-to-circle ratio the
        // smaller sizes use.
        // `text-5xl` brings `line-height: 1`, and React Native has no
        // `line-height: normal` to fall back to, so on native the line box was
        // exactly 48px and cut the tops of Mona's caps (Settings hero). An
        // explicit 60px (1.25em) line box holds the ascenders; web keeps the
        // font's own metrics.
        '4xl': 'text-5xl native:!leading-[60px] ios:translate-y-1',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

export type AvatarVariantProps = VariantProps<typeof avatarVariants>;
